import { EnumAppSignatureType, MApplication, MAppMeta, MProfile } from './scholarship.models';
import { INITIAL_STATE } from './scholarship.state';
import ScholarshipTypes from './scholarship.types';
import {
  createNewProfile,
  generateNewApplication,
  rebuildTtpf,
  removeApplication,
  removeProfile,
  removeSignature,
  sortItems,
  updateApplicationField,
  updateProfileField,
  upsertItems,
  upsertSignatures
} from './scholarship.utils';

// eslint-disable-next-line complexity
const scholarshipReducer = (state = INITIAL_STATE, action) => {
  let sortedApplications;
  switch (action.type) {
    case ScholarshipTypes.APPLICATION_CREATE:
      return {
        ...state,
        application: generateNewApplication(action.payload.userId)
      };
    case ScholarshipTypes.APPLICATION_SAVE_ESSAY:
      return {
        ...state,
        application: {
          ...state.application,
          essay: {
            ...state.application?.essay,
            ...action.payload
          }
        }
      };
    case ScholarshipTypes.APPLICATION_SAVE_TRANSCRIPT:
      return {
        ...state,
        application: {
          ...state.application,
          transcript: action.payload
        }
      };
    case ScholarshipTypes.APPLICATION_SAVE_SIGNATURE: {
      const saveSignatures = state.application!.signatures;
      const saveIndex = saveSignatures.findIndex((s: any) => s.sk === action.payload.sk);
      if (saveIndex !== -1) {
        return {
          ...state
        };
      }

      return {
        ...state,
        application: {
          ...state.application,
          signatures: [...saveSignatures]
        }
      };
    }
    case ScholarshipTypes.APPLICATION_DELETE_FILE_SUCCESS:
      return {
        ...state,
        errorMessage: null,
        isLoading: false,
        application: {
          ...state.application,
          [action.payload.docType]: action.payload
        }
      };
    case ScholarshipTypes.APPLICATION_DELETE_FILE_START:
      return {
        ...state,
        errorMessage: null,
        isLoading: true
      };
    case ScholarshipTypes.APPLICATION_DELETE_FILE_FAILURE:
      return {
        ...state,
        errorMessage: action.payload,
        isLoading: false
      };
    case ScholarshipTypes.APPLICATION_SET_FIELD:
      return {
        ...state,
        error: null,
        application: updateApplicationField(
          state.application,
          action.payload.id,
          action.payload.section,
          action.payload.field,
          action.payload.value
        )
      };
    case ScholarshipTypes.ADD_COUNT:
      return {
        ...state,
        applicationsCount: state.applicationsCount + action.payload
      };
    case ScholarshipTypes.FAILURE:
      return {
        ...state,
        errorMessage: action.payload,
        isLoading: false
      };
    case ScholarshipTypes.DELETE_APPLICATION_START:
      return {
        ...state,
        error: null,
        isFetching: true
      };
    case ScholarshipTypes.DELETE_APPLICATION_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.payload
      };
    case ScholarshipTypes.DELETE_APPLICATION_SUCCESS:
      return {
        ...state,
        isFetching: false,
        error: null,
        applications: state.applications
          ? (removeApplication(state.applications as unknown as MApplication[], action.payload) as unknown as MAppMeta[])
          : undefined
      };
    case ScholarshipTypes.FETCH_APPLICATION_START:
      return {
        ...state,
        isLoading: true,
        application: undefined
      };
    case ScholarshipTypes.FETCH_APPLICATIONS_START:
      return {
        ...state,
        loadingApplications: true
      };
    case ScholarshipTypes.FETCH_APPLICATIONS_FAILURE:
      return {
        ...state,
        error: action.payload,
        loadingApplications: false,
        savingApplication: false
      };
    case ScholarshipTypes.FETCH_APPLICATION_SUCCESS:
      return {
        ...state,
        error: null,
        loadingApplications: false,
        savingApplication: false,
        isLoading: false
      };
    case ScholarshipTypes.FETCH_APPLICATIONS_SUCCESS:
      sortedApplications = sortItems(action.payload, 'firstName', 'asc');

      return {
        ...state,
        error: null,
        applications: sortedApplications,
        loadingApplications: false,
        savingApplication: false,
        isLoading: false,
        parameters: {
          ...state.parameters,
          start: 0
        }
      };
    case ScholarshipTypes.FETCH_PROFILES_SUCCESS:
      return {
        ...state,
        error: null,
        profiles: sortItems(action.payload, 'firstName', 'asc'),
        savingProfile: false
      };
    case ScholarshipTypes.PROFILE_CREATE:
      return {
        ...state,
        error: null,
        profiles: upsertItems(state.profiles, createNewProfile(state.profiles?.[0], action.payload))
      };
    case ScholarshipTypes.PROFILE_DELETE:
      return {
        ...state,
        error: null,
        profiles: state.profiles ? removeProfile(state.profiles, action.payload) : undefined,
        savingProfile: false
      };
    case ScholarshipTypes.PROFILE_SAVE_FAILURE:
      return {
        ...state,
        error: action.payload,
        savingProfile: false,
        savingApplication: false
      };
    case ScholarshipTypes.PROFILE_SAVE_START:
      return {
        ...state,
        error: null,
        savingProfile: true
      };
    case ScholarshipTypes.PROFILE_SAVE_SUCCESS: {
      const updatedProfiles = state.profiles ? removeProfile(state.profiles, 'new') : [];

      return {
        ...state,
        error: null,
        profiles: upsertItems(updatedProfiles, action.payload),
        savingProfile: false
      };
    }
    case ScholarshipTypes.SIGNATURE_SAVE_FAILURE:
      return {
        ...state,
        error: action.payload,
        savingPurchaser: false
      };
    case ScholarshipTypes.SIGNATURE_SAVE_START:
      return {
        ...state,
        error: null,
        savingPurchaser: true
      };
    case ScholarshipTypes.SIGNATURE_SAVE_SUCCESS: {
      const purchaser = state.application!.signatures.filter((signature) => signature.signatureType === EnumAppSignatureType.PURCHASER);
      if (purchaser.length > 0) {
        return {
          ...state,
          error: null,
          savingPurchaser: false,
          application: {
            ...state.application,
            ttpf: {
              ...state.application?.ttpf,
              ttpfPurchaserSignature: rebuildTtpf(purchaser[0] as unknown as Record<string, unknown>)
            }
          }
        };
      } else {
        return {
          ...state,
          error: null,
          savingPurchaser: false
        };
      }
    }

    case ScholarshipTypes.PROFILE_SET_FIELD:
      return {
        ...state,
        error: null,
        profiles: state.profiles
          ? updateProfileField(state.profiles, action.payload.id, action.payload.field, action.payload.value)
          : undefined
      };
    case ScholarshipTypes.RESEND_EMAIL_START:
      return {
        ...state,
        error: null,
        resendEmailStatus: '',
        sendingEmail: true
      };
    case ScholarshipTypes.RESEND_EMAIL_CLOSE:
      return {
        ...state,
        error: null,
        resendEmailStatus: '',
        sendingEmail: false
      };
    case ScholarshipTypes.RESEND_EMAIL_FAILURE:
      return {
        ...state,
        error: action.payload,
        resendEmailStatus: 'failure',
        sendingEmail: false
      };
    case ScholarshipTypes.RESEND_EMAIL_SUCCESS:
      return {
        ...state,
        error: null,
        resendEmailStatus: 'success',
        sendingEmail: false
      };
    case ScholarshipTypes.RESEND_EMAIL_CLOSE_DIALOG:
      return {
        ...state,
        error: null,
        resendEmailStatus: '',
        sendingEmail: false
      };
    case ScholarshipTypes.SECTION_SAVE_START:
      return {
        ...state,
        savingApplication: true
      };
    case ScholarshipTypes.SECTION_SAVE_SUCCESS: {
      const items =
        action.payload.section === 'meta' && state.applications
          ? upsertItems(state.applications as (MProfile | MAppMeta)[], action.payload.record.data)
          : state.applications;
      sortedApplications = action.payload.section === 'meta' && items ? sortItems(items, 'firstName', 'asc') : items;

      return {
        ...state,
        application: updateApplicationField(
          state.application,
          action.payload.record.id,
          action.payload.record.section,
          'root',
          action.payload.record.data
        ),
        applications: sortedApplications as MAppMeta[] | undefined,
        savingProfile: false,
        savingApplication: false
      };
    }
    case ScholarshipTypes.SECTION_SAVE_ESSAY:
      return {
        ...state
      };
    case ScholarshipTypes.SECTION_SAVE_TTPF:
      return {
        ...state,
        application: {
          ...state.application,
          ttpf: {
            ...state.application!.ttpf,
            ttpfParentSignature: rebuildTtpf(action.payload)
          }
        }
      };
    case ScholarshipTypes.SET_APPLICATION_ID:
      return {
        ...state,
        applicationId: action.payload,
        application: {
          ...state.application,
          id: action.payload
        }
      };
    case ScholarshipTypes.SET_STEP: {
      const newCompleted = { ...state.stepsCompleted };

      if (action.payload.oldStep !== undefined) {
        newCompleted[action.payload.oldStep] = true;
      }

      return {
        ...state,
        step: action.payload.newStep,
        stepsCompleted: newCompleted,
        savingApplication: false,
        error: null
      };
    }
    case ScholarshipTypes.SET_STEPS_COMPLETED:
      return {
        ...state,
        stepsCompleted: action.payload
      };
    case ScholarshipTypes.SIGNATURE_UPSERT: {
      const newState = {
        ...state,
        application: {
          ...state.application,
          signatures: upsertSignatures(state.application?.signatures, action.payload)
        }
      };
      console.log(newState);

      return newState;
    }
    case ScholarshipTypes.SIGNATURE_DELETE:
      return {
        ...state,
        application: removeSignature(state.application, action.payload)
      };
    case ScholarshipTypes.ADD_PURCHASER_FAILURE:
      return {
        ...state,
        addPurchaserError: action.payload
      };

    default:
      return state;
  }
};

export default scholarshipReducer;
